import React, { useState } from "react";
import Header from "../pages/header";
import Footer from "../pages/footer";

// Placeholder images
import blog_hero from "../../img/about_3.jpg"; // Use a professional "meeting/interview" image
import author_img from "../../img/testimonial_1.jpg";

const InterviewGuide = () => {
  // State for Accordion
  const [activeId, setActiveId] = useState(null);

  const toggleAccordion = (id) => {
    setActiveId(activeId === id ? null : id);
  };

  // --- THE TOP 20 QUESTIONS DATA ---
  const interviewQuestions = [
    {
      category: "The Basics (Must Know)",
      questions: [
        {
          id: 1,
          q: "Tell me about yourself.",
          a: "Keep it professional, not personal. Use the 'Present, Past, Future' formula. Talk about your current role, your previous experience, and why you are looking for this new opportunity.",
        },
        {
          id: 2,
          q: "What are your greatest strengths?",
          a: "Choose a strength that is relevant to the job description. Back it up with a specific example of how you used that strength to achieve a result.",
        },
        {
          id: 3,
          q: "What is your greatest weakness?",
          a: "Don't say 'I work too hard'. Choose a real weakness, but focus heavily on what you are doing to overcome it. Example: Public speaking, and mention a course you are taking.",
        },
        {
          id: 4,
          q: "Why do you want to work here?",
          a: "Show you've done your research. Mention their company culture, recent news, or mission statement. Connect their goals with your career values.",
        },
        {
          id: 5,
          q: "Where do you see yourself in 5 years?",
          a: "Focus on growth within the company. Employers want to see ambition but also loyalty. 'I hope to have grown into a leadership role within this department...'",
        },
      ],
    },
    {
      category: "Behavioral (The STAR Method)",
      questions: [
        {
          id: 6,
          q: "Tell me about a challenge you faced.",
          a: "Use the STAR method (Situation, Task, Action, Result). Focus on the Action you took and the positive Result that followed.",
        },
        {
          id: 7,
          q: "Describe a time you showed leadership.",
          a: "You don't need a manager title to lead. Talk about a time you stepped up, mentored a colleague, or took ownership of a project when no one else did.",
        },
        {
          id: 8,
          q: "How do you handle conflict with a coworker?",
          a: "Focus on communication and empathy. Explain how you listened to their side, found common ground, and resolved the issue professionally without management intervention.",
        },
        {
          id: 9,
          q: "Tell me about a mistake you made.",
          a: "Own it immediately. Don't make excuses. Explain what happened, how you fixed it, and crucially, what system you put in place to ensure it never happened again.",
        },
        {
          id: 10,
          q: "Describe a time you went above and beyond.",
          a: "Pick a moment where you exceeded expectations. Quantify the result if possible (e.g., 'saved the client $5k' or 'delivered 2 days early').",
        },
      ],
    },
    {
      category: "Tricky & Salary Questions",
      questions: [
        {
          id: 11,
          q: "Why are you leaving your current job?",
          a: "Stay positive. Never badmouth your current employer. Focus on 'seeking new challenges' or 'better growth opportunities' that your current role can't provide.",
        },
        {
          id: 12,
          q: "What are your salary expectations?",
          a: "Don't give a specific number first if you can avoid it. Give a range based on market research (e.g., '$60k - $70k') and say you are open to negotiation based on benefits.",
        },
        {
          id: 13,
          q: "Why is there a gap in your employment?",
          a: "Be honest and brief. Mention constructive things you did during that time: freelancing, upskilling, volunteering, or caretaking. Then pivot back to your readiness to work.",
        },
        {
          id: 14,
          q: "How do you handle stress/pressure?",
          a: "Give concrete examples of your organizational skills. Mention tools you use (like Trello/Asana) or techniques (prioritization) to stay calm and meet deadlines.",
        },
        {
          id: 15,
          q: "Do you have any questions for us?",
          a: "Always say yes! Ask about team culture, upcoming challenges for the department, or what a successful first 90 days looks like.",
        },
      ],
    },
    {
      category: "Rapid Fire / Curveballs",
      questions: [
        {
          id: 16,
          q: "What motivates you?",
          a: "Align this with the job. If it's sales, mention 'hitting targets'. If it's support, mention 'solving complex problems for customers'.",
        },
        {
          id: 17,
          q: "What is your dream job?",
          a: "Keep it related to the industry. You don't want to say 'Professional Surfer' if you are applying for an Accountant role. Connect it to the path you are on.",
        },
        {
          id: 18,
          q: "How do you prioritize your work?",
          a: "Mention the 'Urgent vs Important' matrix. Talk about how you communicate with stakeholders if deadlines are conflicting.",
        },
        {
          id: 19,
          q: "What are you passionate about?",
          a: "This adds personality. It can be professional (coding) or personal (hiking), but explain 'why' it fuels you.",
        },
        {
          id: 20,
          q: "If you were an animal, which one would you be?",
          a: "Pick an animal with traits needed for the job. Ant (hard worker), Lion (leader), Dolphin (intelligent team player).",
        },
      ],
    },
  ];

  return (
    <>
      <Header path="/blog" />

      {/* --- 1. HERO SECTION --- */}
       <div className="container-fluid py-5 bg-dark page-header mb-5">
        <div className="container my-5 pt-5 pb-4">
          <h1 className="display-3 text-white mb-3 animated slideInDown">Top 20 Interview Questions & Answers</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb text-uppercase">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item"><a href="#Career Tips"> Career Tips</a></li>
              <li className="breadcrumb-item"><a href="#Interview Prep">Interview Prep</a></li>
            </ol>
          </nav>
        </div>
      </div>

      {/* --- 2. MAIN CONTENT --- */}
      <div className="container-fluid py-5">
        <div className="container">
          <div className="row g-5">
            {/* Left Column: Questions List */}
            <div className="col-lg-8">
              <div className="mb-5">
                <img
                  className="img-fluid rounded w-100 shadow-sm"
                  src={blog_hero}
                  alt="Interview"
                  style={{ maxHeight: "400px", objectFit: "cover" }}
                />
                <div className="d-flex align-items-center mt-4">
                  <img
                    src={author_img}
                    alt="Author"
                    className="rounded-circle me-3"
                    style={{ width: "50px", height: "50px" }}
                  />
                  <div>
                    <h6 className="mb-0">By Career Mike Ross</h6>
                    <small className="text-muted">
                      Updated for 2025 Market
                    </small>
                  </div>
                </div>
              </div>

              <p className="lead mb-5">
                Nervous about your upcoming interview? You are not alone. We
                have compiled the
                <strong> top 20 most common interview questions</strong> asked
                by HR managers in 2025. Practice these answers to boost your
                confidence.
              </p>

              {/* STAR METHOD BOX */}
              <div className="bg-light border rounded p-4 mb-5 shadow-sm">
                <h4 className="text-primary mb-3">
                  <i className="fa fa-star me-2"></i>The STAR Method
                </h4>
                <p>
                  When answering behavioral questions, use this formula to stay
                  structured:
                </p>
                <div className="row text-center g-3">
                  <div className="col-3">
                    <div className="p-2 border bg-white rounded">
                      <strong>S</strong>ituation
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-2 border bg-white rounded">
                      <strong>T</strong>ask
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-2 border bg-white rounded">
                      <strong>A</strong>ction
                    </div>
                  </div>
                  <div className="col-3">
                    <div className="p-2 border bg-white rounded">
                      <strong>R</strong>esult
                    </div>
                  </div>
                </div>
              </div>

              {/* QUESTIONS ACCORDION */}
              {interviewQuestions.map((section, index) => (
                <div
                  key={index}
                  className="mb-5 wow fadeInUp"
                  data-wow-delay="0.1s"
                >
                  <h3 className="mb-4 border-bottom pb-2">
                    {section.category}
                  </h3>

                  <div className="accordion" id={`accordion-${index}`}>
                    {section.questions.map((item) => (
                      <div
                        className="accordion-item border mb-3 shadow-sm rounded"
                        key={item.id}
                      >
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${
                              activeId === item.id ? "" : "collapsed"
                            }`}
                            type="button"
                            onClick={() => toggleAccordion(item.id)}
                            style={{ fontWeight: "600" }}
                          >
                            <span className="text-primary me-3">
                              #{item.id}
                            </span>{" "}
                            {item.q}
                          </button>
                        </h2>
                        <div
                          className={`accordion-collapse collapse ${
                            activeId === item.id ? "show" : ""
                          }`}
                        >
                          <div className="accordion-body bg-light">
                            <p className="mb-2">
                              <strong>Suggested Answer:</strong>
                            </p>
                            <p className="text-muted mb-3">{item.a}</p>
                            <div className="alert alert-warning border-0 py-2 px-3 mb-0">
                              <small>
                                <i className="fa fa-lightbulb me-2"></i>
                                <strong>Pro Tip:</strong> Keep your answer under
                                2 minutes.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right Column: Sidebar */}
            <div className="col-lg-4">
              {/* PDF Download Widget */}
              <div className="bg-primary text-white rounded p-4 mb-5 text-center wow slideInUp">
                <i className="fa fa-file-pdf fa-4x mb-3"></i>
                <h4 className="text-white">Download Cheat Sheet</h4>
                <p className="text-white-50 mb-4">
                  Get these 20 questions and 10 bonus templates in a printable
                  PDF.
                </p>
                <button className="btn btn-light text-primary fw-bold w-100 py-3">
                  Download PDF
                </button>
              </div>

              {/* Categories */}
              <div className="mb-5 wow slideInUp">
                <h4 className="mb-4">Prep By Role</h4>
                <div className="d-flex flex-wrap gap-2">
                  {[
                    "Software Engineer",
                    "Marketing",
                    "Sales",
                    "Data Analyst",
                    "HR Manager",
                    "Nurse",
                    "Teacher",
                  ].map((tag, i) => (
                    <a
                      href="#"
                      className="btn btn-outline-secondary btn-sm"
                      key={i}
                    >
                      {tag} Q&A
                    </a>
                  ))}
                </div>
              </div>

              {/* Mock Interview Tool Promo */}
              <div className="border rounded p-4 mb-5 text-center bg-white shadow-sm wow slideInUp">
                <i className="fa fa-microphone-alt fa-3x text-primary mb-3"></i>
                <h4>Practice with AI</h4>
                <p className="text-muted mb-4">
                  Record your answers and get instant feedback on your tone and
                  confidence.
                </p>
                <a
                  href="/mock-interview"
                  className="btn btn-outline-primary w-100"
                >
                  Start Mock Interview
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default InterviewGuide;
